import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import KPITile from "../../../../../common/KPITile/KPITile";
import KPIDetailTile from "../../../../../common/KPIDetailTile/KPIDetailTile";
import VarianceValue from "../../../../../common/VarianceValue/VarianceValue";
import GraphCard from "../../../../../common/GraphCard/GraphCard";
import InsightAI from "../../../../../common/InsightAI/InsightAI";
import Skeleton from "../../../../../common/Skeleton/Skeleton";
import isEmpty from "lodash/isEmpty";
import { useProfileParams } from "../../../../../../utils/common";
import { useGetBusinessPerformanceDetailsQuery } from "../../../../../../redux/services/businessPerformaceAPI";
import { useGetAGCAIDetailsMutation } from "../../../../../../redux/services/agcAIAPI";
import styles from "./TotalQsTile.module.scss";
import { useAppSelector } from "../../../../../../redux/hooks";
import { NumberFormatter } from "../../../../../../utils/NumberFormatter";
interface SummaryProp {
  summary?: any;
  loading?: boolean;
}

export const TotalQsTile: React.FC<SummaryProp> = ({ summary, loading = false }) => {
  const { t } = useTranslation();
  const valueNum = typeof summary?.kpiValue === 'number' ? summary.kpiValue : 0;
  const comparisonCriteria = summary?.kpiVarianceComparisonCriteria ?? "yoy";
  const userDetails = useAppSelector((state: any) => state.userDetails.user);
  const [userAffiliateCode] = useState(userDetails?.userAffiliateCode || 0);

  return (
    <KPITile title={t('totalQs.title')} dataTestId="total-qs-tile" loading={loading}>
      <div className={styles.tile_content}>
        <div className={styles.value_wrapper}>
          <div className={styles.value_content}>{NumberFormatter(userAffiliateCode, valueNum)}</div>
          <VarianceValue
            value={summary?.kpiVarianceValue ?? 0}
            dataType={summary?.kpiVarianceDataType ?? "percentage"}
            currency={summary?.kpiCurrency}
            comparisonCriteria={comparisonCriteria}
          />
        </div>
      </div>
    </KPITile>
  );
};

export const TotalQsDetail: React.FC<{ onClose?: () => void }> = ({ onClose }) => {
  const { affiliateCode, aboNumber } = useProfileParams();
  const { t } = useTranslation();
  const kpiCode = "totalSPQualifiedMonthsLos";

  const { data: detailsResponse, isLoading } = useGetBusinessPerformanceDetailsQuery(
    { affiliateCode, aboNumber, payload: { kpis: [{ code: kpiCode }] } as any },
    { skip: !onClose }
  );

  const [getAGCAIDetails, { isLoading: isLoadingAiSummary }] = useGetAGCAIDetailsMutation();
  const [aiContent, setAiContent] = useState<string>("");
  const hasRequestedRef = useRef(false);

  useEffect(() => {
    // Reset ref when modal closes
    if (!onClose) {
      hasRequestedRef.current = false;
      setAiContent("");
      return;
    }

    const canFetch = Number(affiliateCode) !== 0 && aboNumber !== 0 && Boolean(onClose);
    if (!canFetch || hasRequestedRef.current) return;
    
    hasRequestedRef.current = true;
    getAGCAIDetails({ affiliateCode, aboNumber, payload: { kpis: [{ code: kpiCode }] } as any })
      .then((resp: any) => {
        // Handle different response structures
        const responseData = resp?.data || resp;
        const aiRaw = responseData?.body || responseData;
        
        // Try multiple extraction patterns
        const aiResolved = typeof aiRaw === "string" 
          ? aiRaw 
          : aiRaw?.[kpiCode] || aiRaw?.summary || aiRaw || "";
        
        const content = aiResolved ? String(aiResolved).slice(0, 300).concat("...") : "";
        setAiContent(content);
      })
      .catch((error: any) => {
        console.error("Error fetching AI details:", error);
        setAiContent("");
      });
  }, [affiliateCode, aboNumber, onClose, kpiCode, getAGCAIDetails]);


  const detailData = detailsResponse?.body?.kpis?.[0] || {};
  const monthlyViewCharts = detailData?.detail?.monthlyView?.chart || [];

  // Extract line chart data
  const lineChartData = monthlyViewCharts.find(
    (chart: any) => chart.chartType === "lineChart"
  );
  const lineChartDetailData = lineChartData?.chartDetail?.lineChartDetail;
  const currency = detailData?.kpiCurrency || "USD";

  if (isLoading) return <Skeleton height="400px" />;

  const headerContent = (
    <div className={styles.header_content}>
      <div className={styles.tile_header}>
        <div className={styles.title}>{t("totalQs.title")}</div>
        <div className={styles.desc}>{t("totalQs.description")}</div>
      </div>
    </div>
  );

  const leftContent =
  isEmpty(lineChartDetailData) ? (
    <div className={styles.no_data}>{t("noDataAvailable")}</div>
  ) : (
    <GraphCard lineChartData={lineChartDetailData} currency={currency} chartSummaryData={lineChartData?.chartSummary} />
  );

  const rightContent = isLoadingAiSummary ? (
    <Skeleton height="200px" />
  ) : (
    <InsightAI
      updateDate=""
      content={aiContent}
      title={t("ai_summary")}
      showDropdown={false}
      isDropdownEnabled={true}
      flowerIconSize={24}
      titleSize={14}
    />
  );

  return (
    <KPIDetailTile
      headerContent={headerContent}
      leftContent={leftContent}
      rightContent={rightContent}
      onClose={onClose!}
      dataTestId="total-qs-detail"
    />
  );
};

export default TotalQsTile;

